import { API_BASE_URL } from '../../config/onlineConfig';

/**
 * Thrown for any non-2xx response from the online backend. `code` is the
 * stable machine-readable error code the backend always sends alongside
 * `message` (see backend/src/errors/AppError.ts) — UI code should branch on
 * `code`, not parse `message`.
 */
export class ApiError extends Error {
    constructor(message, code, status) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.status = status;
    }
}

/**
 * Low-level fetch wrapper for the online backend — no auth/refresh logic
 * here (that lives in AuthContext.authFetch, which wraps this). Kept
 * separate so AuthContext itself can call the unauthenticated auth
 * endpoints (login/register) through the exact same request/error handling.
 */
export async function apiRequest(path, { method = 'GET', body, token } = {}) {
    let response;
    try {
        response = await fetch(`${API_BASE_URL}${path}`, {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
    } catch (networkError) {
        throw new ApiError(networkError.message || 'Network error', 'NETWORK_ERROR', 0);
    }

    if (response.status === 204) return null;

    let data = null;
    try {
        data = await response.json();
    } catch {
        // A non-JSON body on a non-2xx response still needs to surface as an error below.
    }

    if (!response.ok) {
        throw new ApiError(data?.message || response.statusText, data?.code || 'UNKNOWN_ERROR', response.status);
    }

    return data;
}

export const apiClient = {
    get: (path, token) => apiRequest(path, { method: 'GET', token }),
    post: (path, body, token) => apiRequest(path, { method: 'POST', body, token }),
    patch: (path, body, token) => apiRequest(path, { method: 'PATCH', body, token }),
};
